import { mapGetters, mapActions } from 'vuex'

import parseData from '@/utils/parseData'
import ChartComponent from '@/components/Chart/Chart.vue'
import KPI from '@/components/KPI/KPI.vue'

const PAGE_TYPES = [
  { type: 'product', label: 'Product', color: '#a7c7e7' },
  { type: 'category', label: 'Category', color: '#b7e4c7' },
  { type: 'blog', label: 'Blog', color: '#ffd6a5' },
  { type: 'other', label: 'Other', color: '#cdb4db' }
]

const parseConfig = (config) => {
  if (!config) return null
  if (typeof config !== 'string') return config

  try {
    return JSON.parse(config)
  } catch (error) {
    return null
  }
}

const normalizeLanguageCode = (language) => {
  if (!language || typeof language !== 'string') return null
  const cleaned = language.trim().toLowerCase().replace(/_/g, '-')
  if (!cleaned) return null
  return cleaned.split('-')[0]
}

export const buildPageTypeConfigRows = ({ config = [], language } = {}) => {
  const normalizedLanguage = normalizeLanguageCode(language)

  return PAGE_TYPES.map(pageType => {
    const configRow = config.find(row => {
      return row?.type === pageType.type && normalizeLanguageCode(row?.language) === normalizedLanguage
    })

    return {
      type: pageType.type,
      label: pageType.label,
      regexp: typeof configRow?.regexp === 'string' ? configRow.regexp : ''
    }
  })
}

export const mergePageTypeConfigRows = ({ config = [], language, rows = [] } = {}) => {
  const normalizedLanguage = normalizeLanguageCode(language)
  if (!normalizedLanguage) return config

  const otherLanguageRows = config.filter(row => normalizeLanguageCode(row?.language) !== normalizedLanguage)
  const currentLanguageRows = rows
    .map(row => ({
      type: row.type,
      regexp: typeof row.regexp === 'string' ? row.regexp.trim() : '',
      language: normalizedLanguage
    }))
    .filter(row => row.regexp)

  return [...otherLanguageRows, ...currentLanguageRows]
}

export const buildPageTypeLineChartData = (rows = [], hiddenPageTypes = []) => {
  const dates = [...new Set(rows.map(row => row.date || row.keys?.[0]).filter(Boolean))].sort()
  const hiddenTypes = new Set(hiddenPageTypes)

  return {
    labels: dates,
    datasets: PAGE_TYPES.filter(pageType => !hiddenTypes.has(pageType.type)).map(pageType => ({
      label: pageType.label,
      backgroundColor: pageType.color,
      borderColor: pageType.color,
      pointBackgroundColor: pageType.color,
      fill: false,
      yAxisID: 'y-axis-1',
      data: dates.map(date => {
        const row = rows.find(item => item.type === pageType.type && (item.date || item.keys?.[0]) === date)
        return row?.clicks || 0
      })
    }))
  }
}

export const buildPageTypePieChartData = (rows = []) => {
  return {
    labels: PAGE_TYPES.map(pageType => pageType.label),
    datasets: [{
      backgroundColor: PAGE_TYPES.map(pageType => pageType.color),
      borderColor: '#ffffff',
      borderWidth: 2,
      data: PAGE_TYPES.map(pageType => {
        const row = rows.find(item => item.type === pageType.type)
        return row?.clicks || 0
      })
    }]
  }
}

export const buildPageTypeTableRows = ({ summaryRows = [], filters = [], hiddenPageTypes = [] } = {}) => {
  const hiddenTypes = new Set(hiddenPageTypes)

  return PAGE_TYPES.map(pageType => {
    const summary = summaryRows.find(row => row.type === pageType.type) || {}
    const filter = filters.find(row => row.type === pageType.type) || {}

    return {
      type: pageType.type,
      label: pageType.label,
      urlCount: filter.urlCount,
      source: filter.source,
      truncated: Boolean(filter.truncated),
      noData: Boolean(filter.noData),
      visible: !hiddenTypes.has(pageType.type),
      clicks: summary.clicks || 0,
      impressions: summary.impressions || 0,
      ctr: summary.ctr || 0,
      position: summary.position || 0
    }
  })
}

export default {
  name: 'EcommerceReport',
  components: { ChartComponent, KPI },
  data () {
    return {
      isLoading: {
        report: false,
        pageTypeConfig: false
      },
      date: [],
      language: null,
      languageOptions: [],
      pageTypeConfigDialog: false,
      pageTypeConfigRows: [],
      pageTypeConfigInitialValue: '',
      aggregation: 'weekly',
      hiddenPageTypes: [],
      aggregationOptions: [
        { text: 'Daily', value: 'daily' },
        { text: 'Weekly', value: 'weekly' },
        { text: 'Monthly', value: 'monthly' }
      ],
      chartVersion: 0,
      pageTypeDateRows: [],
      lineChartData: null,
      pieChartData: null,
      lineChartOptions: {
        scales: {
          yAxes: [{
            type: 'linear',
            display: false,
            position: 'left',
            id: 'y-axis-1',
            min: 0,
            ticks: {
              beginAtZero: true
            }
          }],
          xAxes: [{
            type: 'time',
            time: {
              unit: 'week',
              isoWeekday: true,
              displayFormats: {
                day: 'MMM D, YYYY',
                week: 'MMM YYYY',
                month: 'MMM YYYY'
              }
            }
          }]
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          position: 'nearest'
        },
        legend: {
          position: 'bottom'
        },
        animation: {
          duration: 1000,
          easing: 'easeInOutQuart'
        },
        responsive: true,
        maintainAspectRatio: false
      },
      pieChartOptions: {
        legend: {
          position: 'bottom'
        },
        animation: {
          duration: 1000,
          easing: 'easeInOutQuart'
        },
        responsive: true,
        maintainAspectRatio: false
      },
      pageTypeHeaders: [
        { text: 'Type', value: 'label' },
        { text: 'Chart', value: 'chart', sortable: false },
        { text: 'URLs', value: 'urlCount' },
        { text: 'Impressions', value: 'impressions' },
        { text: 'Clicks', value: 'clicks' },
        { text: 'CTR', value: 'ctr' },
        { text: 'Avg pos', value: 'position' },
        { text: 'Status', value: 'status', sortable: false }
      ]
    }
  },
  computed: {
    ...mapGetters([
      'activeContentplan',
      'activeWorkspace',
      'gsc'
    ]),
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    contentplanName () {
      return this.activeContentplan?.name || ''
    },
    totalClicks () {
      return this.gsc?.byPageTypeSummary?.reduce((sum, row) => sum + (row.clicks || 0), 0) || 0
    },
    totalImpressions () {
      return this.gsc?.byPageTypeSummary?.reduce((sum, row) => sum + (row.impressions || 0), 0) || 0
    },
    totalCtr () {
      if (!this.totalImpressions) return 0
      return this.totalClicks / this.totalImpressions * 100
    },
    averagePosition () {
      if (!this.totalImpressions) return 0
      return this.gsc?.byPageTypeSummary?.reduce((sum, row) => sum + (row.position || 0) * (row.impressions || 0), 0) / this.totalImpressions
    },
    pageTypeRows () {
      return buildPageTypeTableRows({
        summaryRows: this.gsc?.byPageTypeSummary || [],
        filters: this.gsc?.meta?.pageType?.filters || [],
        hiddenPageTypes: this.hiddenPageTypes
      })
    },
    hasPageTypeConfigChanges () {
      if (!this.pageTypeConfigDialog) return false
      return JSON.stringify(this.buildPageTypeConfigPayload()) !== this.pageTypeConfigInitialValue
    },
    hasPageTypeConfigValues () {
      return this.pageTypeConfigRows.some(row => row.regexp.trim())
    }
  },
  methods: {
    parseData,
    parseConfig,
    normalizeLanguageCode,
    ...mapActions([
      'contentplanGetOne',
      'contentplanPut',
      'getGSCReportBundle',
      'setSnackbar'
    ]),
    async loadData () {
      document.title = 'Ecommerce report | ContentGecko'
      this.setDates(360)
      this.isLoading.report = true

      try {
        await this.contentplanGetOne({ contentplanId: this.contentplanId })
        this.languageOptions = this.buildLanguageOptions()
        this.language = this.languageOptions[0]?.value || 'en'

        if (!this.activeContentplan?.gsc_property) {
          this.setSnackbar({ open: true, text: 'No GSC integration', type: 'error' })
          return
        }

        await this.refreshReport()
      } catch (error) {
        this.setSnackbar({ open: true, text: this.getErrorMessage(error, 'Could not load ecommerce report'), type: 'error' })
      } finally {
        this.isLoading.report = false
      }
    },
    async refreshReport () {
      this.isLoading.report = true

      try {
        const response = await this.getGSCReportBundle({
          contentplanId: this.contentplanId,
          startDate: this.date[0],
          endDate: this.date[1],
          aggregation: this.aggregation || 'weekly',
          country: 'all',
          language: this.language,
          sections: ['PAGE_TYPE']
        })

        this.pageTypeDateRows = response?.byPageTypeDate || []
        this.lineChartData = buildPageTypeLineChartData(this.pageTypeDateRows, this.hiddenPageTypes)
        this.pieChartData = buildPageTypePieChartData(response?.byPageTypeSummary || [])
        this.chartVersion += 1
      } catch (error) {
        this.setSnackbar({ open: true, text: this.getErrorMessage(error, 'Could not load page-type GSC data'), type: 'error' })
      } finally {
        this.isLoading.report = false
      }
    },
    setDates (days) {
      const today = new Date()
      const endDate = new Date(today)
      endDate.setDate(today.getDate() - 3)

      const startDate = new Date(endDate)
      startDate.setDate(endDate.getDate() - days)

      this.date = [this.formatDate(startDate), this.formatDate(endDate)]
    },
    formatDate (date) {
      const year = date.getFullYear()
      const month = ('0' + (date.getMonth() + 1)).slice(-2)
      const day = ('0' + date.getDate()).slice(-2)
      return `${year}-${month}-${day}`
    },
    buildLanguageOptions () {
      const languages = new Set()
      const contentplanLanguage = this.normalizeLanguageCode(this.activeContentplan?.language)
      languages.add(contentplanLanguage || 'en')

      const translationConfig = this.parseConfig(this.activeContentplan?.translation_config)
      const originalLanguage = this.normalizeLanguageCode(translationConfig?.original?.lang)
      if (originalLanguage) languages.add(originalLanguage)

      translationConfig?.translations?.forEach(translation => {
        const language = this.normalizeLanguageCode(translation?.lang)
        if (language) languages.add(language)
      })

      const pageTypeConfig = this.parseConfig(this.activeContentplan?.page_type_config)
      if (!Array.isArray(pageTypeConfig)) {
        return [...languages].map(language => ({
          text: language.toUpperCase(),
          value: language
        }))
      }

      pageTypeConfig.forEach(row => {
        const language = this.normalizeLanguageCode(row?.language)
        if (language) languages.add(language)
      })

      return [...languages].map(language => ({
        text: language.toUpperCase(),
        value: language
      }))
    },
    getErrorMessage (error, fallback) {
      return error?.response?.data?.message || error?.message || fallback
    },
    formatUrlCount (item) {
      if (item.urlCount === null || item.urlCount === undefined) return '-'
      return parseData(item.urlCount)
    },
    togglePageTypeVisibility (type) {
      if (this.hiddenPageTypes.includes(type)) {
        this.hiddenPageTypes = this.hiddenPageTypes.filter(hiddenType => hiddenType !== type)
      } else {
        this.hiddenPageTypes = [...this.hiddenPageTypes, type]
      }

      this.lineChartData = buildPageTypeLineChartData(this.pageTypeDateRows, this.hiddenPageTypes)
      this.chartVersion += 1
    },
    getPageTypeConfig () {
      const config = this.parseConfig(this.activeContentplan?.page_type_config)
      return Array.isArray(config) ? config : []
    },
    openPageTypeConfigDialog () {
      const config = this.getPageTypeConfig()
      this.pageTypeConfigRows = buildPageTypeConfigRows({ config, language: this.language })
      this.pageTypeConfigInitialValue = JSON.stringify(this.buildPageTypeConfigPayload())
      this.pageTypeConfigDialog = true
    },
    closePageTypeConfigDialog () {
      if (this.isLoading.pageTypeConfig) return
      this.pageTypeConfigDialog = false
    },
    clearPageTypeConfigRows () {
      this.pageTypeConfigRows = this.pageTypeConfigRows.map(row => ({ ...row, regexp: '' }))
    },
    buildPageTypeConfigPayload () {
      return mergePageTypeConfigRows({
        config: this.getPageTypeConfig(),
        language: this.language,
        rows: this.pageTypeConfigRows
      })
    },
    async savePageTypeConfig () {
      if (!this.hasPageTypeConfigChanges) return

      this.isLoading.pageTypeConfig = true

      try {
        const pageTypeConfig = this.buildPageTypeConfigPayload()
        await this.contentplanPut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          page_type_config: JSON.stringify(pageTypeConfig),
          action: 'update'
        })
        await this.contentplanGetOne({ contentplanId: this.contentplanId })
        this.languageOptions = this.buildLanguageOptions()
        this.pageTypeConfigInitialValue = JSON.stringify(pageTypeConfig)
        this.pageTypeConfigDialog = false
        await this.refreshReport()
        this.setSnackbar({ open: true, text: 'Page type filters saved.', type: 'success' })
      } catch (error) {
        this.setSnackbar({ open: true, text: this.getErrorMessage(error, 'Could not save page type filters'), type: 'error' })
      } finally {
        this.isLoading.pageTypeConfig = false
      }
    }
  },
  async mounted () {
    await this.loadData()
  }
}
